import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      if (attempts.current > 8) { setState("timeout"); return; }
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(r.data.status)) { setState("error"); return; }
      } catch (e) {
        console.warn("Payment status poll failed, retrying", e);
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- poll once on mount only
  }, []);

  return (
    <div className="h-screen bg-white text-zinc-900 flex items-center justify-center">
      <div className="text-center max-w-sm">
        {state === "checking" && <><Loader2 size={32} className="animate-spin text-rose-500 mx-auto mb-4" /><p className="text-zinc-500 text-sm" data-testid="payment-checking">Confirming your payment…</p></>}
        {state === "paid" && <>
          <CheckCircle2 size={40} className="text-emerald-500 mx-auto mb-4" />
          <h1 className="font-display font-bold text-2xl mb-2 text-zinc-900" data-testid="payment-success">You're on the Creator Plan</h1>
          <p className="text-zinc-500 text-sm mb-6">One step left: add your OpenAI key so your AI host, commentary, voice and thumbnails can generate. Everything runs on your own key.</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/settings" data-testid="payment-success-add-key" className="bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium rounded-[4px] px-6 py-2.5 transition-colors">Add OpenAI key</Link>
            <Link to="/dashboard" data-testid="payment-success-continue" className="border border-zinc-300 hover:border-zinc-400 text-zinc-800 text-sm font-medium rounded-[4px] px-6 py-2.5 transition-colors">Skip for now</Link>
          </div>
        </>}
        {(state === "error" || state === "timeout") && <>
          <XCircle size={40} className="text-rose-500 mx-auto mb-4" />
          <h1 className="font-display font-bold text-2xl mb-2 text-zinc-900" data-testid="payment-error">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
          <p className="text-zinc-500 text-sm mb-6">{state === "timeout" ? "Check back in a minute — we'll activate your plan as soon as Stripe confirms." : "No charge went through. You can try again anytime."}</p>
          <Link to="/billing" className="border border-zinc-300 text-zinc-800 text-sm rounded-[4px] px-6 py-2.5">Back to billing</Link>
        </>}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="h-screen bg-white text-zinc-900 flex items-center justify-center">
      <div className="text-center">
        <h1 className="font-display font-bold text-2xl mb-2 text-zinc-900" data-testid="payment-cancel">Checkout cancelled</h1>
        <p className="text-zinc-500 text-sm mb-6">Nothing was charged.</p>
        <Link to="/billing" className="border border-zinc-300 text-zinc-800 text-sm rounded-[4px] px-6 py-2.5">Back to billing</Link>
      </div>
    </div>
  );
}
